import { keyBy } from 'lodash';
import React from 'react';
import { Dimensions, Keyboard, StyleSheet, View } from 'react-native';
import { Calendar as RNCCalendar, CalendarProps as RNCCalendarProps } from 'react-native-calendars';
import { overlay } from 'react-native-paper';

import { BigButton, Button } from '@components/Button';
import Modal from '@components/Modal';
import { Caption } from '@components/Typography';
import { useAppTheme } from '@context/ThemeContext';
import {
  dateRangeToString,
  dateToString,
  eachDayOfInterval,
  parsePartialDateRange,
  stringToDate,
} from '@helpers/Dates';
import { DateRange } from '@types';

const CALENDAR_DATE_FORMAT = 'yyyy-MM-dd';
const CALENDAR_ELEVATION = 4;

type CalendarModalProps = Omit<RNCCalendarProps, 'initialDate'> & {
  visible: boolean;
  onDismiss: () => void;
  initialDate?: Date;
  doneButtonTestID?: string;
};

const calendarDateToString = (date: Date) => dateToString(date, CALENDAR_DATE_FORMAT);
const calendarStringToDate = (date: string) => stringToDate(date, CALENDAR_DATE_FORMAT);

export function CalendarModal({
  visible,
  onDismiss,
  initialDate,
  doneButtonTestID,
  ...props
}: CalendarModalProps) {
  const appTheme = useAppTheme();

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      contentContainerStyle={[
        styles.calendarContainer,
        {
          backgroundColor: overlay(CALENDAR_ELEVATION, appTheme.colors.surface),
          borderRadius: appTheme.roundness,
          width: Math.min(Dimensions.get('window').width, appTheme.maxWidth) * 0.9,
        },
      ]}>
      <RNCCalendar
        initialDate={initialDate ? calendarDateToString(initialDate) : undefined}
        theme={{
          backgroundColor: overlay(CALENDAR_ELEVATION, appTheme.colors.surface),
          calendarBackground: overlay(CALENDAR_ELEVATION, appTheme.colors.surface),
          indicatorColor: appTheme.colors.primary,
          arrowColor: appTheme.colors.primary,
          dayTextColor: appTheme.colors.text,
          monthTextColor: appTheme.colors.text,
          todayTextColor: appTheme.colors.primary,
          textDisabledColor: appTheme.colors.disabled,
          textInactiveColor: appTheme.colors.placeholder,
        }}
        style={[styles.calendar, { borderRadius: appTheme.roundness }]}
        {...props}
      />
      <Button
        compact
        mode="text"
        onPress={onDismiss}
        style={styles.doneButton}
        testID={doneButtonTestID}>
        Done
      </Button>
    </Modal>
  );
}

type RangeCalendarModalProps = CalendarModalProps & {
  range?: DateRange;
  onSelect: (range: DateRange) => void;
};

type MarkedDateProps = {
  selected: boolean;
  color: string;
  startingDay?: boolean;
  endingDay?: boolean;
};

type MarkedDates = Record<string, MarkedDateProps>;

export function RangeCalendarModal({ range, onSelect, ...props }: RangeCalendarModalProps) {
  const appTheme = useAppTheme();
  const startDate = range?.startDate;
  const endDate = range?.endDate;
  const selectParsedRange = React.useCallback(
    (partialRange: Partial<DateRange>) => {
      const nextRange = parsePartialDateRange(partialRange);
      if (nextRange) {
        onSelect(nextRange);
      }
    },
    [onSelect]
  );

  const markedDates = React.useMemo<MarkedDates>(() => {
    if (!startDate) {
      return {};
    }

    if (!endDate) {
      return {
        [calendarDateToString(startDate)]: {
          selected: true,
          color: appTheme.colors.primary,
          startingDay: true,
          endingDay: true,
        },
      };
    }

    const interval = eachDayOfInterval({ start: startDate, end: endDate }).map(
      (value, index, array) => ({
        key: calendarDateToString(value),
        selected: true,
        color: appTheme.colors.primary,
        startingDay: index === 0,
        endingDay: index === array.length - 1,
      })
    );
    return keyBy(interval, 'key');
  }, [endDate, appTheme.colors.primary, startDate]);

  return (
    <CalendarModal
      markingType="period"
      markedDates={markedDates}
      onDayPress={(date) => {
        const selectedDate = calendarStringToDate(date.dateString);

        if (!startDate) {
          selectParsedRange({ startDate: selectedDate });
        } else if (!endDate && selectedDate.getTime() < startDate.getTime()) {
          selectParsedRange({ startDate: selectedDate, endDate: startDate });
        } else if (!endDate) {
          selectParsedRange({ startDate, endDate: selectedDate });
        } else {
          selectParsedRange({ startDate: selectedDate });
        }
      }}
      {...props}
    />
  );
}

type RangeCalendarButtonProps = Omit<RangeCalendarModalProps, 'visible' | 'onDismiss'> & {
  placeholder?: string;
  errorText?: string;
};

function getCompletedSingleDateRange(range?: DateRange) {
  if (!range?.startDate || range.endDate) {
    return undefined;
  }

  return {
    startDate: range.startDate,
    endDate: range.startDate,
  };
}

export function RangeCalendarButton({
  range,
  onSelect,
  placeholder,
  errorText,
  testID,
  ...props
}: RangeCalendarButtonProps) {
  const usePlaceholder = placeholder || 'Pick dates';
  const [visible, setVisible] = React.useState(false);
  const latestRange = React.useRef(range);
  const calendarTestID = testID ? `${testID}.Calendar` : undefined;
  const formattedDateRange = range ? dateRangeToString(range) : '';
  const displayText = formattedDateRange || usePlaceholder;

  const appTheme = useAppTheme();

  const errorStyle = {
    ...styles.errorText,
    color: appTheme.colors.error,
  };

  React.useEffect(() => {
    latestRange.current = range;
  }, [range]);

  const handleSelect = React.useCallback(
    (nextRange: DateRange) => {
      latestRange.current = nextRange;
      onSelect(nextRange);
    },
    [onSelect]
  );

  const dismissCalendar = React.useCallback(() => {
    const completedRange = getCompletedSingleDateRange(latestRange.current);
    if (completedRange) {
      latestRange.current = completedRange;
      onSelect(completedRange);
    }

    setVisible(false);
  }, [onSelect]);

  return (
    <View style={styles.buttonContainer}>
      <BigButton
        mode="outlined"
        onPress={() => {
          if (visible) {
            dismissCalendar();
          } else {
            setVisible(true);
            Keyboard.dismiss(); // dismiss keyboard so that it doesn't block calendar
          }
        }}
        textColor={appTheme.colors.text}
        style={[
          styles.button,
          {
            borderColor: errorText ? errorStyle.color : appTheme.colors.placeholder,
            backgroundColor: appTheme.colors.background,
          },
        ]}
        icon="calendar"
        testID={testID}>
        {displayText}
      </BigButton>
      {errorText ? <Caption style={errorStyle}>{errorText}</Caption> : null}
      <RangeCalendarModal
        range={range}
        visible={visible}
        onDismiss={dismissCalendar}
        onSelect={handleSelect}
        testID={calendarTestID}
        doneButtonTestID={calendarTestID ? `${calendarTestID}.Button.Done` : undefined}
        {...props}
      />
    </View>
  );
}

export const styles = StyleSheet.create({
  button: {
    borderWidth: 1,
    marginBottom: 0,
  },
  buttonContainer: {
    flexDirection: 'column',
    marginBottom: 10,
    width: '100%',
  },
  calendar: {},
  calendarContainer: {
    alignSelf: 'center',
    padding: 10,
  },
  doneButton: {
    alignSelf: 'flex-end',
  },
  errorText: {
    paddingHorizontal: 10,
  },
});
